import type { Layer } from "./model";
import { canHaveChildren } from "./model";

/**
 * Attribute carrying a layer's identity through the HTML boundary. `parseDoc`
 * adopts it as the layer id instead of minting a fresh one, so ids survive a
 * serialize → parse round-trip and an SVG → HTML conversion. Overlay
 * animations are keyed by element id, which is what makes this load-bearing.
 */
export const HT_ID_ATTR = "data-ht-id";

/** Attribute carrying a layer's motion (`{ blocks }` JSON) through the HTML boundary. */
export const HT_MOTION_ATTR = "data-ht-motion";

let idCounter = 0;

export function mintId(): string {
  idCounter += 1;
  return `n-${idCounter.toString(36)}`;
}

export function newId(): string {
  return mintId();
}

export function ensureLayerIds(
  layers: readonly Layer[],
  reservedIds: ReadonlySet<string> = new Set(),
): Layer[] {
  const used = new Set(reservedIds);
  const mintUnused = (): string => {
    let id = mintId();
    while (used.has(id)) id = mintId();
    used.add(id);
    return id;
  };
  const visit = (nodes: readonly Layer[]): Layer[] =>
    nodes.map((layer) => {
      const next: Layer = layer.id ? layer : { ...layer, id: mintUnused() };
      if (layer.id) used.add(layer.id);
      if (canHaveChildren(next)) {
        const children = visit(next.children);
        return children === next.children ? next : { ...next, children };
      }
      return next;
    });
  return visit(layers);
}
